import type Phaser from "phaser";
import { portalFfaCatalog } from "../../../data/portal-ffa-catalog";

/** Deterministic LCG for stable terrain patches across reloads. */
function mulberry32(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Procedural map backdrop (no bitmap assets).
 * Layers: base fill, play area, sparse patches, grid hints, rim, corner vignette.
 */
export function drawMapBackdrop(scene: Phaser.Scene): void {
  const { width: mapW, height: mapH, outerMargin: margin } = portalFfaCatalog.map;
  const look = portalFfaCatalog.mapLook;
  const g = scene.add.graphics().setDepth(-10);

  g.fillStyle(look.base, 1);
  g.fillRect(0, 0, mapW, mapH);

  const playX = margin;
  const playY = margin;
  const playW = mapW - margin * 2;
  const playH = mapH - margin * 2;
  g.fillStyle(look.playFill, 1);
  g.fillRect(playX, playY, playW, playH);

  const rand = mulberry32(look.seed);
  for (let i = 0; i < look.patchCount; i++) {
    const px = playX + rand() * playW;
    const py = playY + rand() * playH;
    const r = 40 + rand() * 120;
    g.fillStyle(i % 2 === 0 ? look.patchA : look.patchB, 0.55 + rand() * 0.35);
    g.fillCircle(px, py, r);
  }

  const step = look.gridStep;
  g.lineStyle(1, look.grid, 0.22);
  for (let x = playX; x <= playX + playW; x += step) {
    g.lineBetween(x, playY, x, playY + playH);
  }
  for (let y = playY; y <= playY + playH; y += step) {
    g.lineBetween(playX, y, playX + playW, y);
  }

  // Light hex hint (every other row offset)
  g.lineStyle(1, look.grid, 0.12);
  const hexH = step * 0.866;
  for (let row = 0; row < playH / hexH + 1; row++) {
    const y = playY + row * hexH;
    const ox = row % 2 === 0 ? 0 : step * 0.5;
    for (let col = 0; col < playW / step + 1; col++) {
      const x = playX + ox + col * step;
      g.strokeCircle(x, y, step * 0.28);
    }
  }

  g.lineStyle(3, look.rim, 0.85);
  g.strokeRect(playX, playY, playW, playH);

  const vg = Math.min(mapW, mapH) * 0.18;
  g.fillStyle(look.vignette, 0.55);
  g.fillRect(0, 0, mapW, vg);
  g.fillRect(0, mapH - vg, mapW, vg);
  g.fillRect(0, 0, vg, mapH);
  g.fillRect(mapW - vg, 0, vg, mapH);
}
